import React, { useState } from 'react';
import { useWhatsApp } from '../context/WhatsAppContext';
import QRCard from '../components/QRCard';
import {
  CheckCircle,
  Loader2,
  RefreshCw,
  Smartphone,
  QrCode,
} from 'lucide-react';
import axios from 'axios';

const GenerateQR = () => {
  const { qr, status } = useWhatsApp();

  const [loginMethod, setLoginMethod] = useState('qr');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneState, setPhoneState] = useState({
    step: 'phone',
    displayCode: null,
  });
  const [loading, setLoading] = useState(false);

  const handleRegenerate = async () => {
    try {
      await axios.post('http://localhost:5001/reset-session');
    } catch (err) {
      alert('Failed to reset session: ' + (err.response?.data?.error || err.message));
    }
  };

  const requestPairingCode = async () => {
    if (!phoneNumber.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post('http://localhost:5001/request-pairing-code', {
        number: phoneNumber.trim(),
      });
      const { code } = res.data;

      if (code) {
        // Code-based flow – show the code
        setPhoneState({ step: 'displayCode', displayCode: code });
      } else {
        // Passkey flow – no code, wait for phone approval
        setPhoneState({ step: 'passkey', displayCode: null });
      }
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-start justify-center bg-slate-50 p-6 pt-24">
      <div className="w-full max-w-lg text-center">
        <h1 className="text-3xl font-bold text-[#075e54] mb-6">Link Device</h1>

        {status === 'connected' ? (
          <div className="rounded-2xl bg-green-50 p-8 shadow-lg">
            <CheckCircle size={48} className="mx-auto text-green-600 mb-3" />
            <p className="text-xl font-semibold text-green-800">
              WhatsApp is connected
            </p>
            <p className="mt-2 text-sm text-green-700">
              Your device is ready to send & receive messages.
            </p>
          </div>
        ) : (
          <>
            {/* Method Toggle */}
            <div className="flex justify-center gap-2 mb-6">
              <button
                onClick={() => {
                  setLoginMethod('qr');
                  setPhoneState({ step: 'phone', displayCode: null });
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  loginMethod === 'qr'
                    ? 'bg-green-100 text-green-800 border border-green-300'
                    : 'bg-white text-gray-600 border border-gray-200'
                }`}
              >
                <QrCode size={16} /> QR Code
              </button>
              <button
                onClick={() => {
                  setLoginMethod('phone');
                  setPhoneState({ step: 'phone', displayCode: null });
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  loginMethod === 'phone'
                    ? 'bg-green-100 text-green-800 border border-green-300'
                    : 'bg-white text-gray-600 border border-gray-200'
                }`}
              >
                <Smartphone size={16} /> Phone Number
              </button>
            </div>

            {/* QR Code View */}
            {loginMethod === 'qr' && (
              <>
                {qr ? (
                  <QRCard qr={qr} />
                ) : (
                  <div className="mt-10 flex flex-col items-center">
                    <Loader2 size={40} className="animate-spin text-green-600 mb-4" />
                    <p className="text-lg font-medium text-slate-700">
                      Generating QR code…
                    </p>
                    <button
                      onClick={handleRegenerate}
                      className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition shadow"
                    >
                      <RefreshCw size={16} /> Regenerate QR
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Phone Number Login */}
            {loginMethod === 'phone' && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                {phoneState.step === 'phone' && (
                  <>
                    <p className="text-sm text-gray-500 mb-4">
                      Enter the phone number with country code (e.g., 919876543210)
                    </p>
                    <input
                      type="text"
                      placeholder="Phone number"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-300 mb-4"
                    />
                    <button
                      onClick={requestPairingCode}
                      disabled={loading || !phoneNumber}
                      className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition disabled:opacity-50"
                    >
                      {loading ? <Loader2 size={18} className="animate-spin mx-auto" /> : 'Send Pairing Code'}
                    </button>
                  </>
                )}

               {phoneState.step === 'displayCode' && phoneState.displayCode && (
  <>
    <p className="text-sm text-gray-500 mb-4">
      Enter this code on your phone to link the device:
    </p>

    {/* Code display – individual boxes + dash */}
    <div className="flex items-center justify-center gap-1 mb-4 select-all">
      {/* First 4 characters */}
      {phoneState.displayCode.slice(0, 4).split('').map((char, i) => (
        <span
          key={`a-${i}`}
          className="w-10 h-12 flex items-center justify-center border-2 border-gray-300 rounded-md bg-white text-xl font-mono font-bold text-gray-800"
        >
          {char}
        </span>
      ))}

      {/* Dash separator */}
      <span className="mx-2 text-2xl font-bold text-gray-500">—</span>

      {/* Last 4 characters */}
      {phoneState.displayCode.slice(4, 8).split('').map((char, i) => (
        <span
          key={`b-${i}`}
          className="w-10 h-12 flex items-center justify-center border-2 border-gray-300 rounded-md bg-white text-xl font-mono font-bold text-gray-800"
        >
          {char}
        </span>
      ))}
    </div>

    <p className="text-xs text-gray-400 mb-4">
      Open WhatsApp → <strong>Linked Devices</strong> → <strong>Link a Device</strong> → enter the code above
    </p>

    <button
      onClick={() => setPhoneState({ step: 'phone', displayCode: null })}
      className="text-sm text-gray-500 hover:text-gray-700"
    >
      ← Try a different number
    </button>
  </>
)}

                {phoneState.step === 'passkey' && (
                  <div className="py-8 flex flex-col items-center">
                    <Loader2 size={40} className="animate-spin text-green-600 mb-4" />
                    <p className="text-lg font-medium text-gray-700">
                      Waiting for passkey authentication…
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Approve the passkey prompt on your phone.
                    </p>
                    <button
                      onClick={() => setPhoneState({ step: 'phone', displayCode: null })}
                      className="mt-4 text-sm text-gray-500 hover:text-gray-700"
                    >
                      ← Try a different number
                    </button>
                  </div>
                )}

                {phoneState.step === 'connecting' && (
                  <div className="py-8 flex flex-col items-center">
                    <Loader2 size={40} className="animate-spin text-green-600 mb-4" />
                    <p className="text-lg font-medium text-gray-700">Connecting…</p>
                    <p className="text-sm text-gray-500">Waiting for phone approval</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GenerateQR;